import { theme } from '../constants';
import {
  SYSTEM_FONT_FAMILY,
  fontAssets,
  type FontFamily,
  type LoadedFontFamily,
} from '../constants/fonts';

type ColorTokens = {
  primary: string;
  secondary: string;
  text: string;
  background: string;
};

type StyleSection = {
  colors?: Partial<ColorTokens>;
  background?: string;
  textFont?: FontFamily;
  size?: number;
};

type ResolvedStyleSection = {
  colors: ColorTokens;
  background: string;
  textFont: FontFamily;
  size: number;
};

export type StyleSectionKey =
  | 'common'
  | 'categories'
  | 'reviews'
  | 'product'
  | 'notification'
  | 'button';

const loadedFontFamilies = new Set<LoadedFontFamily>(
  Object.keys(fontAssets) as LoadedFontFamily[],
);

const isLoadedFontFamily = (fontFamily?: string): fontFamily is LoadedFontFamily => {
  return fontFamily != null && loadedFontFamilies.has(fontFamily as LoadedFontFamily);
};

const ensureFontFamily = (
  requestedFont: FontFamily | undefined,
  fallback: FontFamily,
): FontFamily => {
  if (!requestedFont) {
    return fallback;
  }

  if (requestedFont === SYSTEM_FONT_FAMILY) {
    return SYSTEM_FONT_FAMILY;
  }

  return isLoadedFontFamily(requestedFont) ? requestedFont : fallback;
};

const baseFontFamily = ensureFontFamily(
  (theme.fonts.DMSans_400Regular as { fontFamily?: FontFamily }).fontFamily,
  SYSTEM_FONT_FAMILY,
);

const mediumFontFamily = ensureFontFamily(
  (theme.fonts.DMSans_500Medium as { fontFamily?: FontFamily }).fontFamily,
  baseFontFamily,
);

const defaultColors: ColorTokens = {
  primary: theme.colors.textPrimary,
  secondary: theme.colors.accentPrimary,
  text: theme.colors.textPrimary,
  background: theme.colors.backgroundPrimary,
};

const createDefaultSection = (
  overrides: Partial<ResolvedStyleSection> = {},
): ResolvedStyleSection => {
  return {

    colors: { ...defaultColors, ...(overrides.colors ?? {}) },
    background: overrides.background ?? theme.colors.backgroundPrimary,
    textFont: ensureFontFamily(overrides.textFont, baseFontFamily),
    size: overrides.size ?? 14,
  };
};

const DEFAULT_STYLE_SECTIONS: Record<StyleSectionKey, ResolvedStyleSection> = {
  common: createDefaultSection(),
  categories: createDefaultSection({ size: 13 }),
  reviews: createDefaultSection({ size: 12 }),
  product: createDefaultSection({ size: 16 }),
  notification: createDefaultSection(),
  button: createDefaultSection({ size: 16, textFont: mediumFontFamily }),
};

export const STYLE_CONFIG: Record<StyleSectionKey, StyleSection> = {
  common: {},
  categories: {},
  reviews: {},
  product: {},
  notification: {},
  button: {},
};

export const resolveStyleConfig = (
  section: StyleSectionKey,
): ResolvedStyleSection => {
  const overrides = STYLE_CONFIG[section] ?? {};
  const defaults = DEFAULT_STYLE_SECTIONS[section];

  return {
    colors: { ...defaults.colors, ...(overrides.colors ?? {}) },
    background: overrides.background ?? defaults.background,
    textFont: ensureFontFamily(overrides.textFont, defaults.textFont),
    size: overrides.size ?? defaults.size,
  };
};

export type { StyleSection, ResolvedStyleSection };
